import clsx from 'clsx';
import React, { ReactNode, Suspense, useMemo, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import InfoIcon from 'components/assets/info-qmee.svg';
import QmeeCloseButton from 'components/assets/Qmee-close-button.svg';
import Overlay from 'components/atoms/Overlay/Overlay';
import Card from 'components/atoms/Card/Card';
import Txt from 'components/atoms/Txt/Txt';
import ActivityBanner from 'components/molecules/ActivityBanner/ActivityBanner';
import CtaArrow from 'components/assets/cta-arrow.svg';
import LoadingIndicatorSmall from 'components/atoms/LoadingIndicator/LoadingIndicatorSmall';
import { useSubmitPollMutation } from 'hooks/useSubmitPollMutation';
import type { DailyPonderBannerQuery } from './__generated__/DailyPonderBannerQuery.graphql';
import StatisticsIcon from './assets/statistics.svg';
import classes from './DailyPonderBanner.qmee.module.scss';

type HeadingLevel = React.ComponentProps<typeof ActivityBanner>['headingLevel'];

export type DailyPonderBannerProps = {
  title?: ReactNode;
  headingLevel?: HeadingLevel;
  pollId?: string;
  question: string;
  ctaText?: string;
  onInfoClick?: () => void;
  infoAriaLabel?: string;
  showInfoButton?: boolean;
  infoIcon?: ReactNode;
  icon?: ReactNode;
  className?: string;
  headerClassName?: string;
  headerTitleClassName?: string;
  cardClassName?: string;
  questionClassName?: string;
  ctaClassName?: string;
  iconWrapperClassName?: string;
};

const dailyPonderPollQuery = graphql`
  query DailyPonderBannerQuery($id: ID!) {
    node(id: $id) {
      ... on PollQuestion {
        id
        possibleAnswers {
          type
          ... on SelectionQuestionAnswers {
            answers {
              answerKey
              label
            }
          }
          ... on MultiPunchQuestionAnswers {
            answers {
              answerKey
              label
            }
          }
        }
        responseStatistics {
          key
          ratio
          correct
          selected
        }
      }
    }
  }
`;

export default function DailyPonderBanner({
  title,
  headingLevel = 3,
  pollId,
  question,
  ctaText,
  onInfoClick,
  infoAriaLabel = 'More information',
  showInfoButton = true,
  infoIcon,
  icon,
  className,
  headerClassName,
  headerTitleClassName,
  cardClassName,
  questionClassName,
  ctaClassName,
  iconWrapperClassName,
}: DailyPonderBannerProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isInteractive = !!pollId;

  const modalTitle = useMemo(() => {
    if (typeof title === 'string') {
      return title;
    }
    return 'Daily Ponder';
  }, [title]);

  const handleOpenModal = () => {
    if (isInteractive) {
      setIsModalOpen(true);
    }
  };

  const handleBannerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isInteractive) {
      return;
    }
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    const target = event.target as HTMLElement;
    if (target.closest('button')) {
      return;
    }
    event.preventDefault();
    handleOpenModal();
  };

  const infoContent =
    infoIcon === undefined ? <InfoIcon aria-hidden focusable="false" /> : infoIcon;

  const shouldRenderInfoButton = showInfoButton && !!onInfoClick;

  const infoButton =
    shouldRenderInfoButton && infoContent !== null ? (
      <button
        type="button"
        aria-label={infoAriaLabel}
        className={classes.infoButton}
        onClick={(event) => {
          event.stopPropagation();
          onInfoClick();
        }}
      >
        {infoContent}
      </button>
    ) : null;

  const iconNode = icon === undefined ? <StatisticsIcon aria-hidden focusable="false" /> : icon;

  const ctaContent =
    ctaText &&
    (isInteractive ? (
      <button
        type="button"
        className={clsx(classes.cta, ctaClassName)}
        onClick={(event) => {
          event.stopPropagation();
          handleOpenModal();
        }}
      >
        <Txt component="span" className={classes.ctaLabel} skipTranslation>
          {ctaText}
        </Txt>
        <CtaArrow aria-hidden focusable="false" className={classes.ctaIcon} />
      </button>
    ) : (
      <Txt
        component="span"
        className={clsx(classes.cta, classes.ctaStatic, classes.ctaLabel, ctaClassName)}
        skipTranslation
      >
        {ctaText}
        <CtaArrow aria-hidden focusable="false" className={classes.ctaIcon} />
      </Txt>
    ));

  return (
    <>
      <ActivityBanner
        title={title}
        headingLevel={headingLevel}
        infoButton={infoButton}
        cardBackgroundColor="var(--palette-accent-tertiary)"
        cardTextColor="var(--palette-text-primary)"
        className={className}
        headerClassName={headerClassName}
        headerTitleClassName={headerTitleClassName}
        cardClassName={cardClassName}
        role={isInteractive ? 'button' : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        onClick={isInteractive ? handleOpenModal : undefined}
        onKeyDown={handleBannerKeyDown}
      >
        <div className={classes.wrapper}>
          <div className={classes.content}>
            <div className={classes.questionWrapper}>
              <Txt
                className={clsx(classes.question, questionClassName)}
                component="span"
                skipTranslation
              >
                {question}
              </Txt>
              {ctaContent && <div className={classes.ctaWrapper}>{ctaContent}</div>}
            </div>
            <span className={clsx(classes.iconWrapper, iconWrapperClassName)}>{iconNode}</span>
          </div>
        </div>
      </ActivityBanner>

      {isModalOpen && pollId && (
        <Overlay>
          <Card
            className={classes.modalRoot}
            component="div"
            role="dialog"
            aria-modal="true"
            aria-label={modalTitle}
          >
            <div className={classes.modalHeaderYellow}>
              <StatisticsIcon className={classes.modalHeaderIcon} aria-hidden focusable="false" />
              <Txt component="span" className={classes.modalTitle} skipTranslation>
                {modalTitle}
              </Txt>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                aria-label="Close daily ponder"
                className={classes.modalCloseButton}
              >
                <QmeeCloseButton aria-hidden focusable="false" />
              </button>
            </div>

            <Suspense fallback={<LoadingIndicatorSmall />}>
              <DailyPonderPollContent pollId={pollId} question={question} />
            </Suspense>
          </Card>
        </Overlay>
      )}
    </>
  );
}

function DailyPonderPollContent({ pollId, question }: { pollId: string; question: string }) {
  const data = useLazyLoadQuery<DailyPonderBannerQuery>(
    dailyPonderPollQuery,
    { id: pollId },
    { fetchPolicy: 'store-and-network' },
  );
  const [commitMutation, isMutationInFlight] = useSubmitPollMutation();
  const [pendingAnswerId, setPendingAnswerId] = useState<string | null>(null);

  const poll = data.node;
  const responseStatistics = poll?.responseStatistics ?? null;
  const hasAnswered = responseStatistics != null;
  const answers = poll?.possibleAnswers?.answers ?? [];

  const handleAnswerClick = (answerKey: string) => {
    if (hasAnswered || isMutationInFlight) {
      return;
    }
    setPendingAnswerId(answerKey);
    commitMutation({
      variables: { input: { id: pollId, answer: answerKey } },
      onError: () => setPendingAnswerId(null),
    });
  };

  return (
    <div className={classes.modalBody}>
      <Txt component="p" className={classes.modalQuestion} skipTranslation>
        {question}
      </Txt>

      <div className={classes.answers}>
        {answers.map((answer, index) => {
          const statistic = responseStatistics?.find((stat) => stat.key === answer.answerKey);
          const percentage = statistic ? Math.round(statistic.ratio) : null;
          const selected = statistic?.selected ?? pendingAnswerId === answer.answerKey;

          if (hasAnswered) {
            return (
              <div
                key={answer.answerKey}
                className={clsx(
                  classes.answerButton,
                  classes.answerButtonResult,
                  selected && classes.answerButtonSelectedResult,
                )}
              >
                {percentage != null && (
                  <span
                    className={clsx(classes.answerBar, selected && classes.answerBarSelected)}
                    style={{ inlineSize: `${percentage}%` }}
                  />
                )}
                <span className={classes.answerIndex}>{index + 1}</span>
                <Txt component="span" className={classes.answerLabel} skipTranslation>
                  {answer.label}
                </Txt>
                {percentage != null && (
                  <span className={classes.answerPercentage}>{percentage}%</span>
                )}
              </div>
            );
          }

          return (
            <button
              key={answer.answerKey}
              type="button"
              disabled={isMutationInFlight}
              onClick={() => handleAnswerClick(answer.answerKey)}
              className={clsx(
                classes.answerButton,
                pendingAnswerId === answer.answerKey && classes.answerButtonSelected,
              )}
            >
              <span className={classes.answerIndex}>{index + 1}</span>
              <Txt component="span" className={classes.answerLabel} skipTranslation>
                {answer.label}
              </Txt>
            </button>
          );
        })}
      </div>
    </div>
  );
}
